import { CheckCircle, Settings, TriangleAlert } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

import { useAuthStore, useAuthActions } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import {
  fetchUserPreferences,
  updateUserPreferences,
  UserPreferences,
} from '../../services/userService';
import { Button } from '../Common/button';

type ThemePref = NonNullable<UserPreferences['theme']>;
const THEME_OPTIONS: ThemePref[] = ['organization', 'light', 'dark', 'system'];
const LANGUAGE_OPTIONS = ['fr', 'en'] as const;
const DEFAULT_THEME: ThemePref = 'organization';

export const ProfilePreferences: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setTheme } = useTheme();
  const user = useAuthStore((state) => state.user);
  const { updateUser } = useAuthActions();
  const orgDefaultTheme = useBrandingStore((s) => s.branding?.default_theme) ?? null;

  // Resolve a theme preference to a concrete next-themes value.
  const resolveTheme = (theme: ThemePref): 'light' | 'dark' | 'system' =>
    theme === 'organization' ? (orgDefaultTheme ?? 'system') : theme;

  const [prefs, setPrefs] = useState<UserPreferences>({
    theme: DEFAULT_THEME,
    language: 'fr',
    notifications_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const data = await fetchUserPreferences();
        setPrefs({
          theme: (data.theme as ThemePref) || DEFAULT_THEME,
          language: data.language || 'fr',
          notifications_enabled: data.notifications_enabled ?? true,
        });
      } catch (e) {
        console.error('Failed to load preferences:', e);
        if (user?.preferences && typeof user.preferences === 'object') {
          const p = user.preferences as Record<string, unknown>;
          setPrefs({
            theme: (p.theme as ThemePref) || DEFAULT_THEME,
            language: (p.language as 'fr' | 'en') || 'fr',
            notifications_enabled: p.notifications_enabled as boolean ?? true,
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateUserPreferences(prefs);
      const newTheme = (updated.theme as ThemePref) || prefs.theme;
      setPrefs({
        theme: newTheme,
        language: (updated.language as 'fr' | 'en') || prefs.language,
        notifications_enabled: updated.notifications_enabled ?? prefs.notifications_enabled,
      });
      // 'organization' isn't a next-themes value; resolve it to the org default.
      setTheme(resolveTheme(newTheme));
      if (updated.language) {
        await i18n.changeLanguage(updated.language);
      }
      if (user) {
        updateUser({
          ...user,
          preferences: { ...user.preferences, ...updated },
        });
      }
      setSuccess(t('userProfile.preferencesSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      console.error('Failed to save preferences:', e);
      setError(t('userProfile.preferencesError'));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          {t('userProfile.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="text-lg leading-6 font-medium text-foreground mb-4 flex items-center">
        <Settings className="h-5 w-5 mr-2 text-primary" />
        {t('userProfile.preferencesTitle')}
      </h3>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 px-4 py-3 text-destructive">
          <TriangleAlert className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-green-500 bg-green-500/10 px-4 py-3 text-green-600 dark:text-green-400">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('userProfile.themeLabel')}
          </label>
          <div className="flex flex-wrap gap-2">
            {THEME_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, theme: value }))}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  prefs.theme === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {value === 'organization' && t('userProfile.themeOrganization', 'Organisation')}
                {value === 'light' && t('userProfile.themeLight')}
                {value === 'dark' && t('userProfile.themeDark')}
                {value === 'system' && t('userProfile.themeSystem')}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('userProfile.languageLabel')}
          </label>
          <div className="flex flex-wrap gap-2">
            {LANGUAGE_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, language: value }))}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  prefs.language === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {value.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="profile-notifications"
            checked={prefs.notifications_enabled ?? true}
            onChange={(e) =>
              setPrefs((p) => ({ ...p, notifications_enabled: e.target.checked }))
            }
            className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
          />
          <label htmlFor="profile-notifications" className="text-sm font-medium text-foreground">
            {t('userProfile.notificationsLabel')}
          </label>
        </div>
      </div>

      <div className="mt-6">
        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? t('global.saving') : t('userProfile.saveButton')}
        </Button>
      </div>
    </div>
  );
};

export default ProfilePreferences;
